import React, { useState, useMemo, useEffect } from 'react';
import { useExport } from './header/exporters';
import { recordLabel } from '../utils/recordId';
import { useRecordRefs } from '../utils/recordRefs';
import { useNavigate } from 'react-router-dom';
import ContentCard from './ContentCard';
import EmptyState from './EmptyState';
import {
  loadGuidelines,
  refreshGuidelinesFromApi,
  Guideline,
  GuidelineCategory,
  GUIDELINE_CATEGORY_LABELS,
  GUIDELINE_CATEGORIES,
} from '../services/GuidelinesService';
import { fmtId } from '../utils/jiraId';
import { useNotifications } from './NotificationProvider';
import { useHeaderSearch } from '../hooks/useHeaderSearch';
import { usePublishRecordList } from '../hooks/useRecordList';

interface GuidelinesManagerProps {
  categoryFilter?: GuidelineCategory | '';
  onCategoryFilterChange?: (c: GuidelineCategory | '') => void;
}

const GuidelinesManager: React.FC<GuidelinesManagerProps> = ({
  categoryFilter: externalFilter,
  onCategoryFilterChange,
}) => {
  // Re-render when the record numbers (PO-130 instead of PO-90F0D6B4) load.
  useRecordRefs();
  const navigate = useNavigate();
  const { showSuccess } = useNotifications();
  const [guidelines, setGuidelines] = useState<Guideline[]>(() => loadGuidelines());
  const [internalFilter, setInternalFilter] = useState<GuidelineCategory | ''>('');
  const searchTerm = useHeaderSearch();

  useEffect(() => {
    void refreshGuidelinesFromApi().then(setGuidelines);
  }, []);

  // Export button (Header): the active guidelines.
  useExport('guidelines', {
    rows: guidelines.filter(g => !g.archived),
    ctx: { categoryLabel: c => GUIDELINE_CATEGORY_LABELS[c as GuidelineCategory] },
  });
  const filter = externalFilter !== undefined ? externalFilter : internalFilter;
  const setFilter = (f: GuidelineCategory | '') => {
    setInternalFilter(f);
    onCategoryFilterChange?.(f);
  };

  const filtered = useMemo(() => {
    // Archiving a guideline (GuidelineFormPage's delete action) only ever
    // set `archived: true` — nothing here excluded archived rows from the
    // main list, so an "archived" guideline stayed fully visible with no
    // indication anything had changed. Restore now lives in Settings ›
    // Trash; this list should only ever show active guidelines.
    let result = guidelines.filter(g => !g.archived);
    if (filter) result = result.filter(g => g.category === filter);
    const term = searchTerm.trim().toLowerCase();
    if (term) {
      result = result.filter(g =>
        g.title.toLowerCase().includes(term) ||
        g.items.some(i => i.text.toLowerCase().includes(term))
      );
    }
    return result;
  }, [guidelines, filter, searchTerm]);
  usePublishRecordList('guideline', filtered.map(g => g.id));

  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const g of guidelines) {
      if (g.archived) continue;
      counts[g.category] = (counts[g.category] || 0) + 1;
    }
    return counts;
  }, [guidelines]);



  return (
    <div className="wt-container fade-in">
      <div className="vendor-cards-grid">
          {filtered.length === 0 ? (
            <EmptyState title="No guidelines found." description="Add one to get started." compact />
          ) : (
            filtered.map(guideline => (
              <ContentCard
                key={guideline.id}
                hideImage
                title={guideline.title}
                description={guideline.items.slice(0, 3).map(i => (i.emphasis ? `! ${i.text}` : i.text)).join(' · ')}
                tags={[{ label: GUIDELINE_CATEGORY_LABELS[guideline.category] }]}
                details={[
                  { label: 'ID', value: recordLabel('guideline', guideline.id) },
                  { label: 'Items', value: `${guideline.items.length} rules` },
                ]}
                onClick={() => navigate(`/guidelines/${fmtId('GL', guideline.id)}`)}
              />
            ))
          )}
        </div>
    </div>
  );
};

export default GuidelinesManager;
